import type { JSX } from 'react';
import { installPendingWriteNavigationGuard } from './inventory/usePoReceiptWrite';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  Link,
} from '@tanstack/react-router';
import { RootGate } from './shell/RootGate';
import { OfficeShell } from './shell/OfficeShell';
import { TechShell } from './shell/TechShell';
import { useSession } from './session';
import { FIELD_ROLES, JOB_PAGES_ROLES, OFFICE_ONLY, mayEnter } from './nav';
import type { Role } from '@servwell/shared';
import { StubPage } from './pages/StubPage';
import { EmptyState } from './ui/EmptyState';
import { Button } from './ui/Button';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { TodayPage } from './pages/TodayPage';
import { SchedulePage } from './pages/SchedulePage';
import { JobsPage, WorkOrdersPage } from './pages/JobsPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
import { TicketsPage } from './pages/TicketsPage';
import { ticketsSearch } from './tickets/ticketView';
import { AlertsPage } from './pages/AlertsPage';
import { alertsSearch } from './alerts/alertsView';
import { QuotesPage } from './pages/QuotesPage';
import { QuoteDetailPage } from './pages/QuoteDetailPage';
import { MoneyPage } from './pages/MoneyPage';
import { RequestsPage } from './pages/RequestsPage';
import { PipelinePage } from './pages/PipelinePage';
import { SettingsPage } from './pages/SettingsPage';
import { ProductsPage } from './pages/ProductsPage';
import { leaveForMovedCard } from './movedFromSettings';
import { JoinPage } from './pages/JoinPage';
import { PublicInvoicePage } from './pages/PublicInvoicePage';
import { PublicQuotePage } from './pages/PublicQuotePage';
import { SignInPage } from './pages/SignInPage';
import { ConsentPage } from './pages/ConsentPage';
import { ReportsPage } from './pages/ReportsPage';
import { RevenuePage } from './reports/revenue/RevenuePage';
import { ImportPage } from './pages/ImportPage';
import { AgreementDetailPage } from './pages/AgreementDetailPage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { InventoryPage } from './inventory/InventoryPage';
import { PoDetailPage } from './inventory/PoDetailPage';
import { TechTodayPage } from './tech/TechTodayPage';
import { TechVisitPage } from './tech/TechVisitPage';
import { TechVanPage } from './tech/TechVanPage';
import { ProjectsPage } from './projects/ProjectsPage';
import { ProjectDetailPage } from './projects/ProjectDetailPage';
import { EquipmentDetailPage } from './equipment/EquipmentDetailPage';
import { FleetPage } from './fleet/FleetPage';
import { AssetsPage } from './fleet/AssetsPage';
import { AssetDetailPage } from './fleet/AssetDetailPage';
import {
  ASSET_TABS,
  FLEET_TABS,
  CUSTOMER_TABS,
  JOB_TABS,
  commercialJobSearch,
  MONEY_TABS,
  TODAY_TABS,
  tabSearch,
  SCHEDULE_TABS,
  revenueSearch,
  serviceTypeSearch,
  jobsWorkspaceSearch,
  quotesWorkspaceSearch,
} from './ui/pageTabs';
import {
  IconToday,
  IconSchedule,
  IconPipeline,
  IconRequests,
  IconQuotes,
  IconMoney,
  IconInventory,
  IconReports,
  IconWrenchBig,
} from './icons';

/**
 * The v2 route tree — the locked IA (PRD v2 §4), URL-real from day one (router law:
 * every object addressable; the no-URLs failure of v1 dies here, first).
 */

const rootRoute = createRootRoute({
  component: () => (
    <RootGate>
      <Outlet />
    </RootGate>
  ),
  // Last line of defence: an uncaught render error must never be a white screen.
  errorComponent: ({ reset }) => (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md">
        <EmptyState
          icon={IconWrenchBig}
          title="Something broke on this screen"
          description="That's on us, not you. Try again — if it keeps happening, tell us what you were doing."
          action={<Button onClick={reset}>Try again</Button>}
        />
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="font-heading text-2xl font-bold">Page not found</p>
      <p className="text-sm text-muted-foreground">
        That link doesn't go anywhere (anymore).
      </p>
      <Link
        to="/"
        className="text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
      >
        Back to Servwell
      </Link>
    </div>
  ),
});

/** Lands each role on its home surface. */
function RoleHome() {
  const { role } = useSession();
  if (role === 'tech') return <Navigate to="/tech" replace />;
  if (role === 'bookkeeper') return <Navigate to="/money" replace />;
  return <Navigate to="/today" replace />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: RoleHome,
});

/** Accepting an invite. Outside every shell on purpose: the visitor has no org, and often
 *  no account, so neither the office chrome nor the auth gate's redirects apply to them. */
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$token',
  component: JoinPage,
});

/** A customer looking at their own invoice. Outside every shell and the auth gate for the
 *  same reason as /join, only more so: this visitor will never have an account. The token
 *  in the URL is the entire credential. */
const publicInvoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/i/$token',
  component: PublicInvoicePage,
});

/** A customer deciding on a quote. Same reasoning as /i — the visitor has no account. */
const publicQuoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/q/$token',
  component: PublicQuotePage,
});

/**
 * The two screens of an OAuth authorization, outside every shell.
 *
 * Better Auth's provider is configured with `loginPage: '/sign-in'` and
 * `consentPage: '/consent'` and REDIRECTS a browser to them, carrying the signed
 * authorization query. Neither route existed, so both redirects hit the SPA fallback and the
 * flow died in silence — the owner landed in the app, and the connector waited forever.
 *
 * `/sign-in` is ungated for the obvious reason. `/consent` is NOT: there is nobody to grant
 * access until someone is signed in, and the provider only ever sends an authenticated
 * visitor here. What it must not do is run the gate's ORG redirect — see RootGate.
 */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
});

const consentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consent',
  component: ConsentPage,
});

/** Office shell layout — techs are redirected to their own surface. */
function OfficeLayout() {
  const { role } = useSession();
  if (role === 'tech') return <Navigate to="/tech" replace />;
  return (
    <OfficeShell>
      <Outlet />
    </OfficeShell>
  );
}

const officeRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'office',
  component: OfficeLayout,
});

/**
 * Wraps a page so a role that may not enter is REDIRECTED, never shown an error.
 *
 * The distinction is the point: an error says something went wrong and invites a retry.
 * Nothing went wrong — this person simply does not have this page, and sending them to the
 * one they do have is the honest move. `RoleHome` already knows where each role belongs, so
 * this reuses it rather than inventing a second answer.
 *
 * ⚠️ **Invariant:** every `RoleHome` destination must stay reachable by every role that can
 * be sent there, or the redirect is a loop. `/today` IS gated now and does not loop, because
 * the roles sent there (owner, office) are the roles it admits — which is the whole content
 * of the invariant, and the reason `/money` must stay open while a bookkeeper is sent to it.
 * `forbidden-state.test.ts` evaluates this against each route's own parsed roster rather than
 * against a fixed list, so re-gating a home destination on a different list fails there.
 */
function onlyFor(Page: () => JSX.Element, roles: readonly Role[]) {
  function Gated(): JSX.Element {
    const { role } = useSession();
    if (!mayEnter(role, roles)) return <RoleHome />;
    return <Page />;
  }
  // Otherwise every gated route shows up as one indistinguishable frame in DevTools.
  Gated.displayName = `OnlyFor(${Page.name || 'Page'})`;
  return Gated;
}

/**
 * `roles` is the whole reason this helper takes a third argument: gating belongs on the
 * ROUTE, not only on the sidebar link. Hiding a link while leaving the door open is not a
 * permission — `/settings` was reachable by typing it, for `office` and `bookkeeper` alike,
 * the entire time the sidebar was refusing to draw it. (A tech never got that far: the
 * office shell turns them away one level up.)
 */
const office = <
  P extends string,
  S extends Record<string, unknown> = Record<string, never>,
>(
  path: P,
  component: () => JSX.Element,
  roles?: readonly Role[],
  // A tabbed page's `?tab=` contract (105) — validated at the ROUTE for the same reason
  // gating lives here: a deep link is a door, and doors are the router's.
  validateSearch?: (search: Record<string, unknown>) => S,
  // A promised URL whose card moved to another page (CRM-18) is sent on before the page draws.
  beforeLoad?: (ctx: { location: { hash: string } }) => void,
) =>
  createRoute({
    getParentRoute: () => officeRoute,
    path,
    component: roles ? onlyFor(component, roles) : component,
    validateSearch,
    beforeLoad,
  });

/**
 * ⭐ THE OFFICE ROUTES THE SIDEBAR HIDES ARE NOW GATED — for a while `/settings` was the only
 * one that was.
 *
 * (No count here on purpose: an earlier draft of this line said "eighteen", which stopped
 * being true the moment `/projects` came back out. A number in a comment that nothing asserts
 * is a liability, and `forbidden-state.test.ts` derives the real list from `nav.ts`.)
 *
 * Ticket 009 gated `/settings` because the sidebar hid the link while the router left the
 * door open. Nine more nav entries had precisely that shape, and their DETAIL routes — which
 * are in no sidebar at all — had it too. A bookkeeper typing `/jobs` reached the page, every
 * query 403'd, and the screen said "Couldn't load jobs — try again" under a button that
 * could only fail: a permanent authorization state dressed as a transient failure. Nine
 * times over, and the test from 009 pinned the gated list to exactly one entry, so the
 * incompleteness was recorded as deliberate.
 *
 * Most `OFFICE_ONLY` gates below match what the SERVER already enforces for that page's own
 * queries — `visit.board` and `map.day` are `requireRole('owner','office')`, `request.list` and
 * `job.list` ride `job:browse` (CRM 006), `import.preview/commit` are `requireRole('owner','office')`, and
 * the purchase-order router is `inventory:manage`. (Precisely, because an earlier draft was
 * loose: the WRITES on those routers ask for more — `request.approveToQuote` is
 * `quote:create`, `job.create` is `job:create` — and `purchasing.onOrder` asks for
 * `inventory:read`, not `manage`. The gate is set by what the page's PRIMARY READ needs,
 * because that is what decides whether the page can draw at all.) There the router is not inventing a rule; it is refusing to send
 * somebody at a door that is shut.
 *
 * ⚠️ NOT all of them, and an earlier draft of this paragraph claimed otherwise. `/inventory`
 * and `/equipment/$id` sit on reads that are bare `orgProcedure`s (`inventory.onHand`,
 * `inventory.lowStock`, `equipment.get`), and `purchasing.onOrder` asks for `inventory:read`,
 * not `manage`. Those gates are STRICTER than the server, chosen to match the sidebar rather
 * than derived from it — the safe direction, and still a judgement. The server ones are the
 * evidence; these are a decision.
 *
 * ⚠️ **NOT gated, and each on purpose** — a bookkeeper is meant to have these, and
 * `nav.ts` draws them for that role: `/money` and `/invoices/$invoiceId` (`invoice:read`),
 * `/customers` (`customer:browse`) and `/customers/$customerId` (`customer:read`), `/reports` and
 * `/reports/revenue` (`report:read`), and `/projects` + `/projects/$projectId`
 * (`project:read` — see below).
 *
 * ⚠️ **The redirect invariant**: `RoleHome` sends a bookkeeper to `/money` and everyone else
 * to `/today`. Gating a `RoleHome` destination against a role that gets sent there turns the
 * redirect into a loop — `/money` must stay open for exactly that reason, and `/today` is
 * safe only because the roles it admits are the ones sent to it.
 *
 * ⭐ `/projects` is deliberately NOT gated, and a first draft of this change gated it — which
 * would have taken away a capability the matrix explicitly grants. A `bookkeeper` holds
 * `project: ['read']`, given with the note "a project is a P&L view, which is exactly what a
 * bookkeeper is here to read", and `project.list`/`get` enforce exactly that. `nav.ts` hiding
 * the link was the drift, not the permission. Redirecting them from a saved `/projects/:id`
 * P&L link to `/money` is the same class of defect as the nine this ticket closes, pointed
 * the other way. The WRITE controls on both pages now check `project:manage` instead, and the
 * sidebar draws the link for the role that may read it.
 */
const todayRoute = office('/today', TodayPage, OFFICE_ONLY, tabSearch(TODAY_TABS));
/**
 * ⚠️ `tabSearch(SCHEDULE_TABS)` is not optional garnish — it is the OTHER HALF of the
 * contract `useUrlTab` describes. Without it the route strips `?tab=week`, so a link into
 * the week board would silently land on the day board and the page's own hook would keep
 * writing a param the router kept throwing away.
 */
const scheduleRoute = office(
  '/schedule',
  SchedulePage,
  OFFICE_ONLY,
  tabSearch(SCHEDULE_TABS),
);
const pipelineRoute = office('/pipeline', PipelinePage, OFFICE_ONLY, jobsWorkspaceSearch);
const requestsRoute = office('/requests', RequestsPage, OFFICE_ONLY);
const quotesRoute = office('/quotes', QuotesPage, OFFICE_ONLY, quotesWorkspaceSearch);
const quoteDetailRoute = office('/quotes/$quoteId', QuoteDetailPage, OFFICE_ONLY);
/** The list's service type filter is in its address (`/jobs?serviceType=none`, ticket 06). */
const jobsRoute = office('/jobs', JobsPage, JOB_PAGES_ROLES, jobsWorkspaceSearch);
const workOrdersRoute = office(
  '/work-orders',
  WorkOrdersPage,
  JOB_PAGES_ROLES,
  serviceTypeSearch,
);
const jobDetailRoute = office(
  '/jobs/$jobId',
  JobDetailPage,
  JOB_PAGES_ROLES,
  commercialJobSearch,
);
const workOrderDetailRoute = office(
  '/work-orders/$workOrderId',
  WorkOrderDetailPage,
  JOB_PAGES_ROLES,
  tabSearch(JOB_TABS),
);
/**
 * The Tickets page (ops insights Phase 5a, ticket 04): owner and office, the roles that hold
 * `ticket:triage` and work tickets from here (a tech's tickets are on their visit; the
 * bookkeeper has none). Its view, filters and open ticket are in the address, validated here
 * (`ticketsSearch`): `/tickets?ticket=<id>` is the deep link the bell and MCP send.
 */
const ticketsRoute = office('/tickets', TicketsPage, OFFICE_ONLY, ticketsSearch);
/**
 * The Alerts board (ops insights Phase 7, spec §6): owner and office, the roles that hold
 * `alert:read`. The view, the phone's column and the open alert are in the address, validated
 * here (`alertsSearch`): `/alerts?alert=<id>` is the deep link the bell and the morning email
 * send.
 */
const alertsRoute = office('/alerts', AlertsPage, OFFICE_ONLY, alertsSearch);
const projectsRoute = office('/projects', ProjectsPage);
const projectDetailRoute = office('/projects/$projectId', ProjectDetailPage);
const equipmentDetailRoute = office(
  '/equipment/$equipmentId',
  EquipmentDetailPage,
  OFFICE_ONLY,
);
/**
 * The company register (107). OFFICE_ONLY, which is STRICTER than the server on purpose
 * and for a reason the ⚠️ paragraph above names: `asset:read` includes `tech`, row-scoped
 * to the van they are actually driving. That scoping exists so a field surface can use
 * it — it is not a licence to hand a tech the office's fleet page.
 *
 * ⚠️ NOT because every read there would 403: the detail page calls `asset.get`, which is
 * gated on `asset:read`, and its response embeds both the maintenance and assignment
 * histories. An earlier version of this comment claimed the fleet page's maintenance and
 * assignment reads "all ask for `asset:manage`" — the MUTATING ones do. The route gate is
 * therefore doing real work rather than merely echoing the server, which is the argument
 * for it, and the reason it must not be softened to match `asset:read`. One card at a
 * time. A tech who types `/fleet/assets` is REDIRECTED to their own day, never shown a
 * page of refusals.
 */
/**
 * The fleet page itself (112). OFFICE_ONLY on the same argument as the register below,
 * one step stronger: this route's own read is gated on `asset:read-fleet`, a verb only
 * owner and office hold, because the day it returns carries every customer's name and
 * street address. The sidebar hiding a link is not a permission — `forbidden-state`
 * checks this list against the nav's, by source text.
 */
const fleetRoute = office('/fleet', FleetPage, OFFICE_ONLY, tabSearch(FLEET_TABS));
const assetsRoute = office(
  '/fleet/assets',
  AssetsPage,
  OFFICE_ONLY,
  tabSearch(ASSET_TABS),
);
const assetDetailRoute = office('/fleet/assets/$assetId', AssetDetailPage, OFFICE_ONLY);
const moneyRoute = office('/money', MoneyPage, undefined, tabSearch(MONEY_TABS));
const invoiceDetailRoute = office('/invoices/$invoiceId', InvoiceDetailPage);
const customersRoute = office('/customers', CustomersPage);
const customerDetailRoute = office(
  '/customers/$customerId',
  CustomerDetailPage,
  undefined,
  tabSearch(CUSTOMER_TABS),
);
/**
 * Products & services (CRM-18), the price book's own page. OFFICE_ONLY, as its server is:
 * every `priceBook.*` procedure is `requireRole('owner', 'office')`.
 */
const productsRoute = office('/products', ProductsPage, OFFICE_ONLY);
const inventoryRoute = office('/inventory', InventoryPage, OFFICE_ONLY);
const poDetailRoute = office(
  '/purchase-orders/$purchaseOrderId',
  PoDetailPage,
  OFFICE_ONLY,
);
const reportsRoute = office('/reports', ReportsPage);
/**
 * The Revenue page (ops insights Phase 2), reached from the top of Money in the Reports menu.
 * UNGATED here, as `/reports` is: the server's `report:read` is what decides, and a bookkeeper
 * holds it. A tech never gets this far — the office shell turns them away one level up. The
 * period and the Invoiced | Collected basis live in the address (`?period=`, `?basis=`),
 * validated here like a tab (`revenueSearch`).
 */
const revenueRoute = office('/reports/revenue', RevenuePage, undefined, revenueSearch);
// `office` is admitted because the server already authorises it for everything on the page;
// a bookkeeper is sent home rather than shown fifteen cards collapsed into a single 403. The
// sidebar link stays owner-only until the nav rework moves Settings out of the footer —
// office reaching this page without a link is the state that rework exists to fix.
// `/settings#price-book` leaves for Products & services, where that card lives now.
const settingsRoute = office(
  '/settings',
  SettingsPage,
  OFFICE_ONLY,
  undefined,
  leaveForMovedCard,
);
const importRoute = office('/import', ImportPage, OFFICE_ONLY);
const agreementDetailRoute = office(
  '/agreements/$agreementId',
  AgreementDetailPage,
  OFFICE_ONLY,
);

/**
 * Tech surface — separate shell, same tokens.
 *
 * ⚠️ Gated on the SHELL, which covers `/tech`, `/tech/visits/$visitId` and `/tech/van` in one
 * place: this route is their parent, so a child cannot be added that misses the guard. That
 * matters more here than on the office side, where every page is declared through `office()`
 * and the drift detector reads them all.
 *
 * Ungated until now, for every authenticated role. A bookkeeper reached the field shell and
 * then "You don't have access to the field schedule" — and `/tech/van` drew a stock list with
 * a Restock button whose mutation 403s, because the inventory reads behind it are bare
 * `orgProcedure`s. See `FIELD_ROLES` for who is admitted and why the office is.
 */
const techRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tech',
  component: onlyFor(
    () => (
      <TechShell>
        <Outlet />
      </TechShell>
    ),
    FIELD_ROLES,
  ),
});

const techIndexRoute = createRoute({
  getParentRoute: () => techRoute,
  path: '/',
  component: TechTodayPage,
});

const techVisitRoute = createRoute({
  getParentRoute: () => techRoute,
  path: '/visits/$visitId',
  component: TechVisitPage,
});

/** What's on the truck, and how to get more onto it (§6.2). */
const techVanRoute = createRoute({
  getParentRoute: () => techRoute,
  path: '/van',
  component: TechVanPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  joinRoute,
  publicInvoiceRoute,
  publicQuoteRoute,
  signInRoute,
  consentRoute,
  officeRoute.addChildren([
    todayRoute,
    scheduleRoute,
    pipelineRoute,
    requestsRoute,
    quotesRoute,
    quoteDetailRoute,
    jobsRoute,
    workOrdersRoute,
    jobDetailRoute,
    workOrderDetailRoute,
    ticketsRoute,
    alertsRoute,
    projectsRoute,
    projectDetailRoute,
    equipmentDetailRoute,
    fleetRoute,
    assetsRoute,
    assetDetailRoute,
    moneyRoute,
    invoiceDetailRoute,
    customersRoute,
    customerDetailRoute,
    productsRoute,
    inventoryRoute,
    poDetailRoute,
    reportsRoute,
    revenueRoute,
    settingsRoute,
    importRoute,
    agreementDetailRoute,
  ]),
  techRoute.addChildren([techIndexRoute, techVisitRoute, techVanRoute]),
]);

installPendingWriteNavigationGuard();
export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
